import * as Select from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { BlockType } from '../types'

const OPTIONS: ReadonlyArray<{ value: BlockType; label: string; hint: string }> = [
  { value: 'account', label: 'Account', hint: 'Bank + NUBAN' },
  { value: 'customer', label: 'Customer', hint: 'Internal customer ID' },
  { value: 'card', label: 'Card', hint: 'Card token (no PAN)' },
  { value: 'bvn', label: 'BVN', hint: 'Hashed in your browser' },
  { value: 'device', label: 'Device', hint: 'Device fingerprint id' },
  { value: 'ip', label: 'IP', hint: 'Single IP address' },
  { value: 'terminal', label: 'Terminal', hint: 'POS terminal id' },
]

interface Props {
  value: BlockType
  onChange: (next: BlockType) => void
  id?: string
}

export function BlockTypeSelect({ value, onChange, id }: Props) {
  const current = OPTIONS.find((o) => o.value === value) ?? OPTIONS[0]!
  return (
    <Select.Root value={value} onValueChange={(v) => onChange(v as BlockType)}>
      <Select.Trigger
        id={id}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded border border-input bg-bg px-3 text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
        aria-label="Block type"
      >
        <span className="flex items-center gap-2">
          <span className="font-medium">{current.label}</span>
          <span className="text-xs text-muted-fg">{current.hint}</span>
        </span>
        <Select.Icon>
          <ChevronDown size={14} className="text-muted-fg" aria-hidden />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          sideOffset={4}
          className="z-50 min-w-[16rem] rounded-md border border-border bg-popover p-1"
        >
          <Select.Viewport>
            {OPTIONS.map((opt) => (
              <Select.Item
                key={opt.value}
                value={opt.value}
                className="flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted"
              >
                <span className="flex flex-col">
                  <Select.ItemText className="font-medium">{opt.label}</Select.ItemText>
                  <span className="text-xs text-muted-fg">{opt.hint}</span>
                </span>
                <Select.ItemIndicator>
                  <Check size={12} className="text-primary" />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}
